import { ZoomIn } from "lucide-react";
import React from "react";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import type { ResumeContextValue } from "@/app/types/resume";
import Spinner from "../../components/Spinner";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import ResumeContext from "../../context/ResumeContext";
import { getTemplateName } from "../../handlers/utilityFunctions";
import { cn } from "../../lib/utils";
import { LevelsOfExperience } from "../../models/resume/template";
import { StaticValue } from "../../seed/constants";
import mapping from "../../seed/metadata/filemapping";
import { NavigationFlow, NavigationList } from "../../seed/navigation";
import { ResumeDataService } from "../../services/create-resume/resume-data";

interface TemplateEntry {
	templateName: string;
	templatePath: string;
	templateIcon: string;
}

interface TemplateGridProps {
	templates: TemplateEntry[];
	selectedTemplateName: string;
	actionLabel: string;
	isLoading: boolean;
	onPick: (templateName: string) => void;
	onView: (event: React.MouseEvent, templateIcon: string) => void;
}

function TemplateGrid({ templates, selectedTemplateName, actionLabel, isLoading, onPick, onView }: TemplateGridProps) {
	return (
		<div className="grid grid-cols-1 justify-items-center gap-6 sm:grid-cols-2 md:grid-cols-3">
			{templates.map((template) => (
				<div
					key={template.templateName}
					onClick={() => onPick(template.templateName)}
					className={cn(
						"group relative cursor-pointer overflow-hidden rounded-lg border bg-white shadow-sm transition-shadow hover:shadow-md",
						selectedTemplateName === template.templateName
							? "border-primary-600 ring-2 ring-primary-200"
							: "border-gray-200",
					)}
				>
					<img
						src={`../images/resume-template/${template.templateIcon}`}
						alt={template.templateName}
						className="w-full"
					/>

					<div className="absolute inset-0 flex items-center justify-center gap-3 bg-black/40 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
						<Button
							type="button"
							variant="secondary"
							size="icon"
							aria-label="Preview template"
							onClick={(event) => onView(event, template.templateIcon)}
						>
							<ZoomIn className="h-5 w-5" />
						</Button>
						<Button type="button">{isLoading ? <Spinner /> : actionLabel}</Button>
					</div>
				</div>
			))}
		</div>
	);
}

function SelectResumeTemplate() {
	const premiumTemplatesSettings = mapping["Premium Templates Configurations"][0];
	const [showPremiumTemplate] = React.useState(premiumTemplatesSettings.showPremiumTemplates);
	const [selectedTemplateImage, setSelectedTemplateImage] = React.useState("");
	const [showSelectedTemplateImage, setShowSelectedTemplateImage] = React.useState(false);
	const [isLoading, setIsLoading] = React.useState(false);
	const urlParams = new URLSearchParams(window.location.search);
	const templateId = urlParams.get("templateId");
	const journalReferenceId = urlParams.get("journalReferenceId");
	let templateName = "";
	const navigate = useNavigate();
	const template = useSelector(
		(state: { TemplateSelectionReducer: { selectedTemplate: { yearsOfExperience: string } } }) => {
			return state.TemplateSelectionReducer;
		},
	);
	const leveOfExperience = template.selectedTemplate;
	const levelsObject = new LevelsOfExperience();
	const listOfExperience = levelsObject.listOfExperience;

	const selectedExperience = listOfExperience.find((e) => {
		return e.name === leveOfExperience.yearsOfExperience;
	});

	const uniqueListOfExperiences: TemplateEntry[] = [...new Set(listOfExperience.flatMap((ex) => ex.templateList))];

	const premiumTemplatesList: TemplateEntry[] = [...new Set(listOfExperience.flatMap((ex) => ex.premiumTemplateList))];

	const navigationFlow = new NavigationFlow();

	const fresherFlow = navigationFlow.fresherFlow;
	const experiencedFlow = navigationFlow.experiencedFlow;
	const noExperienceList = new NavigationList(fresherFlow, fresherFlow);
	const experiencedList = new NavigationList(experiencedFlow, experiencedFlow);
	const templateNavigation =
		leveOfExperience.yearsOfExperience === "No Experience" ? noExperienceList : experiencedList;

	const dispatch = useDispatch();
	const { fetchResumeData } = React.useContext(ResumeContext) as ResumeContextValue;
	const [cookies] = useCookies<string>(["resume"]);
	const staticValues = new StaticValue().getDefaultValues();

	if (templateId) {
		templateName = getTemplateName(templateId);
	}

	function onSelectTemplate(selectedTemplateName: string) {
		localStorage.setItem(
			"templateName",
			selectedTemplateName === "experienced" ? "fresher_template" : selectedTemplateName,
		);
		dispatch({
			type: "CHANGE_TEMPLATE_DETAILS",
			payload: {
				selectedTemplate: {
					yearsOfExperience: leveOfExperience.yearsOfExperience,
					templateName: selectedTemplateName === "experienced" ? "fresher_template" : selectedTemplateName,
				},
				navigationFlow: templateNavigation,
			},
		});
		navigate("/resume/contact-information");
	}

	function onChangeTemplate(selectedTemplateName: string) {
		setIsLoading(true);
		const resumeDataService = new ResumeDataService();
		resumeDataService.changeTemplateName(
			{
				journaReferenceId: journalReferenceId,
				templateId: selectedTemplateName,
			},
			() => {
				setIsLoading(false);
				// The template changed on the server. RouteHandler fetches the resume
				// only once on mount, so re-fetch the context before we navigate to the
				// review page. This replaces the previous full-page reload.
				const resumeId = cookies[staticValues.localStorageResumeId];
				if (resumeId) {
					fetchResumeData(resumeId, () => navigate("/resume/review"));
				} else {
					navigate("/resume/review");
				}
			},
			(error: { response: { data: { message: string } } }) => {
				setIsLoading(false);
				toast.error(error.response.data.message);
			},
		);
	}

	const onPickTemplate = (selectedTemplateName: string) => {
		if (templateId) {
			onChangeTemplate(selectedTemplateName);
		} else {
			onSelectTemplate(selectedTemplateName);
		}
	};

	const onViewTemplate = (event: React.MouseEvent, selectedTemplateName: string) => {
		event.stopPropagation();
		setSelectedTemplateImage(selectedTemplateName);
		setShowSelectedTemplateImage(true);
	};

	const actionLabel = templateId ? "Use Template" : "Select Template";

	return (
		<div className="py-8 md:py-12">
			<div className="mx-auto max-w-6xl px-4">
				<div className="mb-6 text-center">
					<h2 className="text-2xl font-bold uppercase text-gray-900 md:text-3xl">Choose from our resume templates</h2>
				</div>

				<Tabs defaultValue="Recommended" className="items-center">
					<TabsList className="mx-auto h-auto gap-2 rounded-none border-b bg-transparent px-0 py-1 text-foreground">
						<TabsTrigger
							value="All"
							className="after:-mb-1 relative after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 hover:bg-accent hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:hover:bg-accent data-[state=active]:after:bg-primary"
						>
							All
						</TabsTrigger>
						<TabsTrigger
							value="Recommended"
							className="after:-mb-1 relative after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 hover:bg-accent hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:hover:bg-accent data-[state=active]:after:bg-primary"
						>
							Recommended
						</TabsTrigger>
						<TabsTrigger
							value="Premium"
							className="after:-mb-1 relative after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 hover:bg-accent hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:hover:bg-accent data-[state=active]:after:bg-primary"
						>
							Premium
						</TabsTrigger>
					</TabsList>

					<TabsContent value="All" className="pt-4">
						<TemplateGrid
							templates={uniqueListOfExperiences}
							selectedTemplateName={templateName}
							actionLabel={actionLabel}
							isLoading={isLoading}
							onPick={onPickTemplate}
							onView={onViewTemplate}
						/>
					</TabsContent>

					<TabsContent value="Recommended" className="pt-4">
						<TemplateGrid
							templates={selectedExperience.templateList}
							selectedTemplateName={templateName}
							actionLabel={actionLabel}
							isLoading={isLoading}
							onPick={onPickTemplate}
							onView={onViewTemplate}
						/>
					</TabsContent>

					<TabsContent value="Premium" className="pt-4">
						{showPremiumTemplate ? (
							<TemplateGrid
								templates={premiumTemplatesList}
								selectedTemplateName={templateName}
								actionLabel={actionLabel}
								isLoading={isLoading}
								onPick={onPickTemplate}
								onView={onViewTemplate}
							/>
						) : (
							<p className="text-center text-xl font-medium text-gray-900">Coming soon</p>
						)}
					</TabsContent>
				</Tabs>
			</div>

			<Dialog open={showSelectedTemplateImage} onOpenChange={setShowSelectedTemplateImage}>
				<DialogContent className="p-4 sm:max-w-3xl" aria-describedby={undefined}>
					<DialogTitle className="sr-only">Template preview</DialogTitle>
					<img src={`../images/resume-template/${selectedTemplateImage}`} alt="" className="w-full rounded-lg" />
				</DialogContent>
			</Dialog>
		</div>
	);
}

export default SelectResumeTemplate;
